import { MAX_AXLE_QUANTITY, type TyreSelection } from '../lib/tyres'

export interface TyreAxleQuantitiesProps {
  /** A staggered selection: `rear` is set. */
  selection: TyreSelection
  onSelectionChange: (selection: TyreSelection) => void
}

const OPTIONS = Array.from({ length: MAX_AXLE_QUANTITY + 1 }, (_, index) => index)

/**
 * How many tyres on each axle of a staggered car (ACA-98).
 *
 * Rears often wear first on these cars, so either axle can be none, but not
 * both: a booking for no tyres is not a tyre booking.
 */
export function TyreAxleQuantities({ selection, onSelectionChange }: TyreAxleQuantitiesProps) {
  const rear = selection.rear
  if (!rear) return null

  const axles = [
    {
      name: 'Front tyres',
      size: selection.size.size,
      value: selection.quantity,
      other: rear.quantity,
      set: (quantity: number) => onSelectionChange({ ...selection, quantity }),
    },
    {
      name: 'Rear tyres',
      size: rear.size,
      value: rear.quantity,
      other: selection.quantity,
      set: (quantity: number) => onSelectionChange({ ...selection, rear: { ...rear, quantity } }),
    },
  ]

  return (
    <div className="ac-stack ac-stack--tight">
      <p className="ac-label">How many tyres?</p>
      {axles.map((axle) => (
        <div key={axle.name} className="ac-axle">
          <p className="ac-axle__name">
            {axle.name} <span className="ac-axle__size">{axle.size}</span>
          </p>
          <div className="ac-quantity ac-quantity--axle" role="group" aria-label={axle.name}>
            {OPTIONS.map((count) => {
              const selected = axle.value === count
              return (
                <button
                  key={count}
                  type="button"
                  className={`ac-quantity__option${selected ? ' ac-quantity__option--selected' : ''}`}
                  aria-pressed={selected}
                  disabled={count === 0 && axle.other === 0}
                  onClick={() => axle.set(count)}
                >
                  {count}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
